import React from 'react';
import { CalendarDays, TrendingUp, Percent, Clock, CreditCard, PiggyBank, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета кредита
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Расчет процента переплаты от суммы кредита
  const overpaymentPercent = (results.totalCreditCost / results.loanAmount) * 100;

  return (
    <ToolResult title="Результаты расчета кредита">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Ежемесячный платеж */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <CalendarDays className="w-4 h-4"/>
            Ежемесячный платеж
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {/* Показываем диапазон для дифференцированных платежей, иначе первый платеж */}
              {Math.abs(results.firstMonthlyPayment - results.lastMonthlyPayment) > 0.01
                ? `${formatCurrency(results.firstMonthlyPayment)} → ${formatCurrency(results.lastMonthlyPayment)}`
                : formatCurrency(results.firstMonthlyPayment)
              }
            </span>
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            {Math.abs(results.firstMonthlyPayment - results.lastMonthlyPayment) > 0.01 
              ? 'Сумма уменьшается с течением времени (дифференцированный платеж)' 
              : 'Фиксированный платеж на весь срок (аннуитетный)'}
          </p>
        </div>

        {/* Переплата и полная стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Переплата по кредиту
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-rose-800 dark:text-rose-200" aria-live="polite">
              {formatCurrency(results.totalCreditCost)}
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              ({formatNumber(overpaymentPercent, 1)}% от суммы)
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Включает проценты{results.totalFees > 0 ? ' и комиссии' : ''}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Сумма кредита" 
          value={formatCurrency(results.loanAmount)} 
          color="indigo" 
          icon={<CreditCard className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Процентная ставка" 
          value={`${formatNumber(results.interestRate, 2)}% годовых`} 
          color="orange" 
          icon={<Percent className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Срок кредита" 
          value={`${results.loanTermYears} ${formatYearsLabel(results.loanTermYears)} (${results.loanTermMonths} мес.)`} 
          icon={<Clock className="w-4 h-4"/>} 
        />
        
        {results.effectiveTermMonths !== results.loanTermMonths && (
          <ResultItem 
            label="Фактический срок погашения" 
            value={`${Math.floor(results.effectiveTermMonths / 12)} ${formatYearsLabel(Math.floor(results.effectiveTermMonths / 12))} ${results.effectiveTermMonths % 12} мес.`} 
            color="green"
            icon={<Clock className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="Тип платежа" 
          value={results.paymentType === 'annuity' ? 'Аннуитетный (равными платежами)' : 'Дифференцированный (уменьшающийся)'} 
        />
        
        <ResultItem 
          label="Общая сумма выплат" 
          value={formatCurrency(results.totalPayment)} 
        />
        
        <ResultItem 
          label="Переплата по процентам" 
          value={formatCurrency(results.totalInterest)} 
          color="orange" 
        />
        
        {results.totalFees > 0 && (
          <ResultItem 
            label="Комиссии и сборы" 
            value={formatCurrency(results.totalFees)} 
            color="orange" 
          />
        )}
        
        {results.totalEarlyRepayment > 0 && (
          <ResultItem 
            label="Досрочное погашение (всего)" 
            value={formatCurrency(results.totalEarlyRepayment)} 
            color="green" 
            icon={<PiggyBank className="w-4 h-4"/>} 
          />
        )}

        {/* Информация о ПСК */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <AlertCircle className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Полная стоимость кредита (ПСК)</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                ПСК = <span className="font-medium text-orange-600 dark:text-orange-400">{formatNumber(results.effectiveRate || overpaymentPercent, 2)}% годовых</span>. 
                Реальная стоимость кредита с учетом всех платежей.
              </p>
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const formatYearsLabel = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;